import axios from 'axios'
import { allPass, find, prop, toPairs } from 'ramda'
import { isNumber } from 'ramda-adjunct'

const S3_IMAGE_HOST = import.meta.env.S3_IMAGE_HOST

const paramNames = {}
const allowedParams = {
  format: true,
  width: true,
  height: true,
  fit: true,
  mode: true,
  time: true,
}
const valueNotEmpty = ([, value]) => value !== '' && value !== undefined && value !== null
const paramPermitted = ([key]) => allowedParams[key]
const createTransformation = (options) =>
  toPairs(options)
    .filter(valueNotEmpty)
    .filter(paramPermitted)
    .map(([name, value]) => (paramNames[name] || name) + '=' + value)
    .join(',')

export const identify = prop('filename')
export const exists = allPass([Boolean, prop('path'), identify])

export const isImageType = (image) => image?.mimetype?.startsWith('image/') && image?.mimetype !== 'image/gif'
export const isVideoType = (image) => image?.mimetype?.startsWith('video/') || image?.mimetype === 'image/gif'
export const firstValidResource = find(exists)

export const originalFilename = prop('originalname')

export const url = (options, file) => {
  if (!file) {
    file = options
    options = null
  }

  if (!file) {
    return ''
  }

  const isVideo = isVideoType(file)

  if (options) {
    // Translate from cloudinary to cloudflare.
    if (options.crop === 'fill') {
      delete options.crop
      options.fit = 'cover'
    }
    if (options.crop === 'pad') {
      delete options.crop
      options.fit = isVideo ? 'contain' : 'pad'
    }

    if (isVideo) {
      // Format is not supported for video transformation: https://developers.cloudflare.com/stream/transform-videos/#format
      delete options.format

      if (options.width) {
        options.width = 2 * Math.floor(options.width / 2)
      }
      if (options.height) {
        options.height = 2 * Math.floor(options.height / 2)
      }

      if (isNumber(options.start_offset)) {
        options.time = options.start_offset + 's'
      }
      if (options.time) {
        options.mode = options.mode || 'frame'
      }
    }
  }

  let { path, filename } = file
  if (path.startsWith('/')) {
    path = path.slice(1)
  }

  let transformation = []

  if (isVideo) {
    transformation = ['cdn-cgi/media', createTransformation(options)]
  } else if (isImageType(file)) {
    transformation = ['cdn-cgi/image', createTransformation(options)]
  }

  return [S3_IMAGE_HOST, ...transformation, path, filename].filter(Boolean).join('/')
}

export const upload = async (file, { signUpload, path }) => {
  // Here we're handing signed uploads directly to s3/r2.
  const { signedUrl, filename, bucket } = await signUpload({ type: 'r2', path })

  try {
    const response = await axios.put(signedUrl, file, { headers: { 'Content-Type': file.type } })

    const result = {
      size: file.size,
      mimetype: file.type,
      path,
      originalname: file.name,
      filename,
      bucket,
      etag: response.headers.etag,
    }

    return result
  } catch (error) {
    console.error(error, error.response)
    throw error
  }
}
